import { useMemo, useState } from "react";
import { Slider } from "./ui/slider";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Check, ChevronsUpDown, X, Search } from "lucide-react";
import { SOURATES, LEVEL_LABELS } from "../lib/sourates";

/**
 * Slider de niveau 0..5 avec libellés (Aucun → Bilingue)
 */
export function LevelSlider({ value, onChange, label, testId, max = 5 }) {
  const v = Number.isFinite(value) ? value : 0;
  return (
    <div className="space-y-2 min-w-0">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-[#1A2547]">{label}</span>
        <Badge className="bg-[#E0E7FF] text-[#1E3A8A] hover:bg-[#E0E7FF] border-0 rounded-full whitespace-nowrap">
          {LEVEL_LABELS[v] || `Niveau ${v}`}
        </Badge>
      </div>
      <Slider
        min={0}
        max={max}
        step={1}
        value={[v]}
        onValueChange={(arr) => onChange(arr[0])}
        className="w-full"
        data-testid={testId}
      />
      {/* Légende compacte : début / milieu / fin uniquement pour éviter le débordement */}
      <div className="flex justify-between text-[10px] uppercase tracking-[0.15em] text-[#8a968f]">
        <span>{LEVEL_LABELS[0]}</span>
        <span>{LEVEL_LABELS[Math.floor(max / 2)]}</span>
        <span>{LEVEL_LABELS[max]}</span>
      </div>
    </div>
  );
}

/**
 * Slider 0..114 pour nombre de sourates
 */
export function SourateCountSlider({ value, onChange, testId }) {
  const v = Number.isFinite(value) ? value : 0;
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-[#1A2547]">Nombre de sourates connues</span>
        <Badge className="bg-[#FBE8DC] text-[#C86444] hover:bg-[#FBE8DC] border-0 rounded-full">
          {v} / 114
        </Badge>
      </div>
      <Slider min={0} max={114} step={1} value={[v]} onValueChange={(arr) => onChange(arr[0])} className="w-full" data-testid={testId} />
      <div className="flex justify-between text-[10px] uppercase tracking-[0.15em] text-[#8a968f]">
        <span>0</span><span>30</span><span>60</span><span>90</span><span>114</span>
      </div>
    </div>
  );
}

/**
 * Multi-sélection des 114 sourates connues
 * value : tableau de numéros (ex: [1, 113, 114])
 */
export function SourateSelector({ value = [], onChange, testId }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const selected = useMemo(() => Array.isArray(value) ? value : [], [value]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return SOURATES;
    return SOURATES.filter((s) =>
      s.name.toLowerCase().includes(q) ||
      String(s.num) === q ||
      s.name_ar.includes(search.trim())
    );
  }, [search]);

  const toggle = (num) => {
    const cur = selected.includes(num) ? selected.filter((x) => x !== num) : [...selected, num].sort((a, b) => a - b);
    onChange(cur);
  };
  const removeOne = (num) => onChange(selected.filter((x) => x !== num));
  const clearAll = () => onChange([]);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-[#1A2547]">Sourates connues (sélectionner)</span>
        {selected.length > 0 && (
          <button type="button" onClick={clearAll} className="text-[11px] text-[#B0413E] hover:underline" data-testid={`${testId}-clear`}>
            Tout désélectionner
          </button>
        )}
      </div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            className="w-full justify-between rounded-xl font-normal h-auto py-2.5"
            data-testid={testId}
          >
            <span className={selected.length === 0 ? "text-[#8a92a3]" : "text-[#1A2547]"}>
              {selected.length === 0 ? "Cliquez pour choisir…" : `${selected.length} sourate${selected.length > 1 ? "s" : ""} sélectionnée${selected.length > 1 ? "s" : ""}`}
            </span>
            <ChevronsUpDown className="ml-2 w-4 h-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] max-h-80 overflow-hidden p-0" align="start">
          <div className="border-b border-[#EAE6DF] p-2">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-[#8a92a3]" />
              <Input
                placeholder="Rechercher (n°, nom, arabe)…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-9 pl-8 text-sm rounded-lg"
                data-testid={`${testId}-search`}
              />
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <div className="py-6 text-center text-xs text-[#8a92a3]">Aucune sourate trouvée.</div>
            ) : (
              filtered.map((s) => {
                const isSelected = selected.includes(s.num);
                return (
                  <button
                    type="button"
                    key={s.num}
                    onClick={() => toggle(s.num)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-[#FCFBF8] text-left ${isSelected ? "bg-[#F4F1EB]" : ""}`}
                    data-testid={`${testId}-option-${s.num}`}
                  >
                    <span className={`w-4 h-4 rounded-md border ${isSelected ? "bg-[#1E3A8A] border-[#1E3A8A]" : "border-[#C5CAD8]"} flex items-center justify-center shrink-0`}>
                      {isSelected && <Check className="w-3 h-3 text-white" />}
                    </span>
                    <span className="text-[10px] text-[#8a92a3] w-6 shrink-0">{s.num}.</span>
                    <span className="flex-1 truncate text-[#1A2547]">{s.name}</span>
                    <span className="text-xs text-[#5c6374] font-[Outfit] shrink-0" dir="rtl">{s.name_ar}</span>
                  </button>
                );
              })
            )}
          </div>
        </PopoverContent>
      </Popover>

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid={`${testId}-tags`}>
          {selected.map((num) => {
            const s = SOURATES.find((x) => x.num === num);
            if (!s) return null;
            return (
              <span key={num} className="inline-flex items-center gap-1 bg-[#E0E7FF] text-[#1E3A8A] rounded-full px-2 py-0.5 text-xs">
                {s.num}. {s.name}
                <button type="button" onClick={() => removeOne(num)} className="hover:bg-[#1E3A8A]/15 rounded-full p-0.5">
                  <X className="w-3 h-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Bloc complet "Niveau d'arabe & sourates" pour le formulaire d'inscription
 */
export function ArabicLevelBlock({ form, set, testIdPrefix = "arabe" }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      <LevelSlider
        label="Niveau d'arabe écrit"
        value={form.niveau_arabe_ecrit || 0}
        onChange={(v) => set("niveau_arabe_ecrit", v)}
        testId={`${testIdPrefix}-ecrit`}
      />
      <LevelSlider
        label="Niveau d'arabe oral"
        value={form.niveau_arabe_oral || 0}
        onChange={(v) => set("niveau_arabe_oral", v)}
        testId={`${testIdPrefix}-oral`}
      />
      <SourateCountSlider
        value={form.nb_sourates || 0}
        onChange={(v) => set("nb_sourates", v)}
        testId={`${testIdPrefix}-nb-sourates`}
      />
      <SourateSelector
        value={form.sourates_connues || []}
        onChange={(arr) => set("sourates_connues", arr)}
        testId={`${testIdPrefix}-sourates-list`}
      />
    </div>
  );
}
